import { useEffect, useMemo, useState } from 'react'
import { Box, Button, Paper, Stack, Switch, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { useTranslation } from 'react-i18next'
import StepLayout from './StepLayout'
import FormField from './FormField'
import WizardTextField from './WizardTextField'
import RespondentPreview from './RespondentPreview'
import BasicStimulusPicker from './BasicStimulusPicker'
import { selectedMetric, useFixedMetric } from './fixedMetric'
import {
  DEFAULT_EXPOSURE_MS,
  MAX_STIMULUS_MINUTES,
  MAX_STIMULUS_SECONDS,
  basicReadyToLaunch,
  formatClock,
  soleStimulus,
  videoFileDurationSeconds,
} from './basicStudy'
import { youTubeDurationSeconds, youTubeId } from '@/utils/youtube'
import type { MetricKey } from './fixedMetric'
import type { DraftPatch, StimulusDraft, StudyDraft } from './types'

type BasicStudySetupProps = {
  draft: StudyDraft
  onChange: (patch: DraftPatch) => void
  onPatchStimulus: (localId: string, patch: Partial<StimulusDraft>) => void
  onRemoveStimulus: (localId: string) => void
  onLaunch: () => void
  launching: boolean
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function Card({ children }: { children: React.ReactNode }) {
  const theme = useTheme()
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: `${theme.affexy.radii.lg / 1.33}px`,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.25,
      }}
    >
      {children}
    </Paper>
  )
}

function SectionHeading({ title, desc }: { title: string; desc: string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box>
      <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 15.5 }}>{title}</Typography>
      <Typography sx={{ fontSize: 13, color: c.ink56, mt: 0.25, lineHeight: 1.5 }}>{desc}</Typography>
    </Box>
  )
}

/**
 * FaceWise sets a study up on one screen: a name, one material, the fixed
 * metric shown but not configured, and how the link is shared. Everything the
 * four Pro steps ask beyond that belongs to a research programme, which this
 * customer does not have.
 */
export default function BasicStudySetup({
  draft,
  onChange,
  onPatchStimulus,
  onRemoveStimulus,
  onLaunch,
  launching,
}: BasicStudySetupProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const fixedMetric = useFixedMetric()

  const stimulus = soleStimulus(draft)
  // Reading a picked file's length takes a moment; a pasted link takes longer.
  const [measuringFile, setMeasuringFile] = useState(false)
  const [rejected, setRejected] = useState<string | null>(null)

  // The metric questions are never part of the draft (see fixedMetric.ts), so
  // the preview gets a copy of the draft with the switched-on ones grafted on —
  // it must show respondents the journey they will actually take, including
  // the one with no survey at the end.
  const previewDraft = useMemo<StudyDraft>(
    () => ({
      ...draft,
      demographics: selectedMetric(fixedMetric, draft.metricFields),
      questions: [],
      questionTiming: 'end',
    }),
    [draft, fixedMetric],
  )

  /** Ask this field, or stop asking it. The order respondents meet the
   *  questions comes from METRIC_KEYS, not from the order they were switched
   *  on, so the stored list is rebuilt rather than appended to. */
  const toggleMetric = (key: MetricKey, on: boolean) => {
    onChange({
      metricFields: fixedMetric
        .map((field) => field.key)
        .filter((k) => (k === key ? on : draft.metricFields.includes(k))),
    })
  }

  // --- How long is the material? --------------------------------------------
  // A pasted YouTube link is measured here, before the study is built around
  // it. Files are measured at pick time; an over-long one is never taken into
  // the draft, so autosave cannot upload it.
  const stimulusId = stimulus?.id
  const videoId = stimulus?.type === 'url' ? youTubeId(stimulus.url) : null
  const knownDuration = stimulus?.durationSeconds
  const [checkingLink, setCheckingLink] = useState(false)

  useEffect(() => {
    if (!stimulusId || !videoId || knownDuration != null) {
      setCheckingLink(false)
      return
    }
    let cancelled = false
    setCheckingLink(true)
    // Wait for the customer to stop typing before booting a player.
    const timer = window.setTimeout(() => {
      void youTubeDurationSeconds(videoId).then((seconds) => {
        if (cancelled) return
        setCheckingLink(false)
        // An unreadable length is left unset: the upload endpoint has the final
        // say, and refusing a study on our own uncertainty would be worse.
        if (seconds != null) onPatchStimulus(stimulusId, { durationSeconds: seconds })
      })
    }, 700)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [stimulusId, videoId, knownDuration, onPatchStimulus])

  /** Put `next` in the material slot, taking the previous one off the study
   *  (server-side too) so a swap cannot leave both attached. */
  const replaceStimulus = (next: StimulusDraft) => {
    if (stimulus) onRemoveStimulus(stimulus.id)
    onChange({ stimuli: [next] })
  }

  const handlePickFile = async (file: File, type: 'video' | 'image') => {
    setRejected(null)
    let durationSeconds: number | null = null
    if (type === 'video') {
      setMeasuringFile(true)
      durationSeconds = await videoFileDurationSeconds(file)
      setMeasuringFile(false)
      if (durationSeconds != null && durationSeconds > MAX_STIMULUS_SECONDS) {
        setRejected(
          t('wizard.oneScreen.material.rejectedTooLong', {
            minutes: MAX_STIMULUS_MINUTES,
            length: formatClock(durationSeconds),
          }),
        )
        return
      }
    }
    replaceStimulus({
      id: `stim-${Date.now()}-${type}`,
      type,
      name: file.name,
      label: '#1',
      meta: formatBytes(file.size),
      status: 'ready',
      file,
      // FaceWise has no exposure field — an image simply gets the default.
      ...(type === 'image' ? { exposureMs: DEFAULT_EXPOSURE_MS } : {}),
      ...(durationSeconds != null ? { durationSeconds } : {}),
    })
  }

  const handleAdd = (type: 'text' | 'url') => {
    setRejected(null)
    replaceStimulus(
      type === 'url'
        ? {
            id: `stim-${Date.now()}-url`,
            type: 'url',
            name: '',
            label: '#1',
            meta: t('wizard.stimulus.urlMeta'),
            status: 'ready',
            url: '',
          }
        : {
            id: `stim-${Date.now()}-text`,
            type: 'text',
            name: t('wizard.stimulus.types.text.label'),
            label: '#1',
            meta: '',
            status: 'ready',
            payload: '',
            exposureMs: DEFAULT_EXPOSURE_MS,
          },
    )
  }

  const ready = basicReadyToLaunch(draft)

  return (
    <StepLayout
      title={t('wizard.oneScreen.title')}
      subtitle={t('wizard.oneScreen.subtitle')}
      asideWidth={380}
      aside={<RespondentPreview variant="tour" draft={previewDraft} />}
    >
      <Card>
        <FormField label={t('wizard.oneScreen.nameLabel')} hint={t('wizard.oneScreen.nameHint')} required>
          <WizardTextField
            value={draft.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder={t('wizard.oneScreen.namePlaceholder')}
          />
        </FormField>
      </Card>

      <Card>
        <SectionHeading
          title={t('wizard.oneScreen.material.title')}
          desc={t('wizard.oneScreen.material.desc', { minutes: MAX_STIMULUS_MINUTES })}
        />
        <BasicStimulusPicker
          stimulus={stimulus}
          checking={measuringFile || checkingLink}
          rejected={rejected}
          onPickFile={(file, type) => void handlePickFile(file, type)}
          onAdd={handleAdd}
          onUpdate={(patch) => stimulus && onPatchStimulus(stimulus.id, patch)}
          onRemove={() => {
            setRejected(null)
            if (stimulus) onRemoveStimulus(stimulus.id)
          }}
        />
      </Card>

      <Card>
        <SectionHeading title={t('wizard.oneScreen.survey.title')} desc={t('wizard.oneScreen.survey.desc')} />
        <Box
          sx={{
            bgcolor: c.gray50,
            border: `1px solid ${c.ink08}`,
            borderRadius: `${theme.affexy.radii.md}px`,
            p: 2,
          }}
        >
          <Stack spacing={2}>
            {fixedMetric.map(({ key, question }) => {
              const on = draft.metricFields.includes(key)
              return (
                <Box key={question.id} sx={{ opacity: on ? 1 : 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <PersonOutlineIcon sx={{ fontSize: 16, color: on ? c.coral : c.ink56 }} />
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, flex: 1 }}>{question.text}</Typography>
                    <Typography sx={{ fontSize: 12, color: c.ink56 }}>
                      {t(on ? 'wizard.oneScreen.survey.asked' : 'wizard.oneScreen.survey.skipped')}
                    </Typography>
                    <Switch
                      size="small"
                      color="primary"
                      checked={on}
                      onChange={(e) => toggleMetric(key, e.target.checked)}
                      inputProps={{ 'aria-label': t('wizard.oneScreen.survey.toggle', { field: question.text }) }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: 3 }}>
                    {question.options.map((o) => (
                      <Box
                        key={o.id}
                        sx={{
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 34,
                          bgcolor: '#fff',
                          border: `1px solid ${c.ink08}`,
                          fontSize: 12.5,
                          color: c.ink72,
                          textDecoration: on ? 'none' : 'line-through',
                        }}
                      >
                        {o.text}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )
            })}
          </Stack>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 17, color: c.success, mt: '1px' }} />
          <Typography sx={{ fontSize: 12.5, color: c.ink72, lineHeight: 1.5 }}>
            {t(draft.metricFields.length > 0 ? 'wizard.oneScreen.survey.note' : 'wizard.oneScreen.survey.noneNote')}
          </Typography>
        </Box>
      </Card>

      <Card>
        <SectionHeading title={t('wizard.oneScreen.sharing.title')} desc={t('wizard.oneScreen.sharing.desc')} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            p: '14px 16px',
            bgcolor: c.gray50,
            border: `1.5px solid ${c.ink08}`,
            borderRadius: '14px',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: c.coral, mt: '1px' }} />
          <Typography sx={{ fontSize: 13.5, color: c.ink72, lineHeight: 1.5 }}>
            {t('wizard.oneScreen.sharing.linkNote')}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <FormField label={t('wizard.oneScreen.sharing.limitLabel')} hint={t('wizard.oneScreen.sharing.limitHint')}>
            <WizardTextField
              type="number"
              value={draft.responseLimit}
              // The response limit is this measurement's target, so the two are
              // one number in FaceWise — the study list and the respondent
              // counter both read `target_respondents`.
              onChange={(e) => onChange({ responseLimit: e.target.value, targetRespondents: e.target.value })}
              suffix={t('wizard.oneScreen.sharing.limitSuffix')}
            />
          </FormField>
          <FormField label={t('wizard.oneScreen.sharing.expiryLabel')} hint={t('wizard.oneScreen.sharing.expiryHint')}>
            <WizardTextField
              type="date"
              value={draft.expiry}
              onChange={(e) => onChange({ expiry: e.target.value })}
              slotProps={{
                input: { startAdornment: <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: c.ink56, mr: 1 }} /> },
              }}
            />
          </FormField>
        </Box>
      </Card>

      <Paper
        sx={{
          background: theme.affexy.gradients.ctaCard,
          borderRadius: `${theme.affexy.radii.lg / 1.33}px`,
          p: 2.75,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box sx={{ width: 50, height: 50, borderRadius: '14px', bgcolor: 'rgba(234,80,72,.2)', display: 'grid', placeItems: 'center', flex: '0 0 50px' }}>
          <PlayArrowIcon sx={{ fontSize: 24, color: c.coral }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 16 }}>
            {t(ready ? 'wizard.oneScreen.launch.ready' : 'wizard.oneScreen.launch.notReady')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,.72)' }}>
            {t(ready ? 'wizard.oneScreen.launch.readyBody' : 'wizard.oneScreen.launch.notReadyBody')}
          </Typography>
        </Box>
        <Button variant="contained" size="large" onClick={onLaunch} disabled={launching || !ready} endIcon={<PlayArrowIcon />}>
          {t(launching ? 'wizard.footer.launching' : 'wizard.footer.launch')}
        </Button>
      </Paper>
    </StepLayout>
  )
}
